import * as rdfjs from "@rdfjs/types";
import * as src from '../ontologies/src';
import { RdfStore } from "rdf-stores";
import { rdf, RDF } from '../ontologies';
import { _RDF, _RDFA, _RDFS, _OWL, _SH, _SKOS, _SPARQL, _XSD } from "../ontologies";
import { EventEmitter } from "stream";
import { Reasoner } from "./reasoners/reasoner";
import { RdfXmlParser } from "rdfxml-streaming-parser";
import { graph, serialize } from "rdflib";
import { toRdflibTerm } from "./utils";
import { TurtleLexer, TurtleParser, TurtleReader, NQuadsLexer, NQuadsParser, NQuadsReader, createFileBlankNodeIdGenerator } from "@faubulous/mentor-rdf-parsers";
import { DatasetView } from "./dataset-view";

/**
 * Indicates an error when a triple is not found in the store.
 */
export class TripleNotFoundError extends Error {
    /**
     * Create a new instance of the error.
     * @param message The error message.
     */
    constructor(subject: rdfjs.Quad_Subject | null, predicate: rdfjs.Quad_Predicate | null, object: rdfjs.Quad_Object | null) {
        super(`No triple was found matching the pattern: ${subject} ${predicate} ${object}`);

        this.name = "TripleNotFoundError";
    }
}

/*
 * A store for RDF triples with support for reasoning.
 */
export class Store implements rdfjs.DatasetCore<rdfjs.Quad> {
    /**
     * The adapted RDF.js triple store implementation.
     * 
     * Note: Please do not use this directly, but rather use the `_ds` property to access the dataset.
     */
    private readonly _store = RdfStore.createDefault();

    /**
     * The RDF dataset containing the triples in the store, which should primarily be used
     * for any operations so that the store can be swapped out with a different implementation.
     */
    private readonly _ds: rdfjs.DatasetCore = this._store.asDataset();

    /**
     * The reasoner to be used for inference.
     */
    readonly reasoner?: Reasoner;

    /**
     * A monotonic counter that is incremented on every mutation of the store.
     */
    private _version = 0;

    /**
     * Monotonic counters per graph URI, incremented on every mutation of the graph.
     */
    private readonly _graphVersions = new Map<string, number>();

    /**
     * Get a monotonic counter that is incremented on every mutation of the store.
     * Can be used to cheaply detect whether the store has changed.
     */
    get version(): number {
        return this._version;
    }

    /**
     * Get the number of triples in all graphs of the store.
     */
    get size(): number {
        return this._ds.size;
    }

    /**
     * Get the RDF.js DataFactory used by the store.
     */
    get dataFactory(): rdfjs.DataFactory {
        return this._store.dataFactory;
    }

    /**
     * Create a new RDF triple store.
     * @param reasoner The reasoner to be used for inference.
     */
    constructor(reasoner?: Reasoner) {
        this.reasoner = reasoner;
    }

    [Symbol.iterator](): Iterator<rdfjs.Quad, any, any> {
        return this._ds[Symbol.iterator]();
    }

    /**
     * Get a monotonic counter for a single graph, incremented on every mutation of the graph.
     * Can be used to cheaply detect whether the contents of a graph have changed.
     * @param graphUri A graph URI.
     * @returns The current version of the graph; `0` if the graph was never modified.
     */
    getGraphVersion(graphUri: string): number {
        return this._graphVersions.get(graphUri) ?? 0;
    }

    private _touchGraph(graphUri: string): void {
        this._version++;
        this._graphVersions.set(graphUri, (this._graphVersions.get(graphUri) ?? 0) + 1);
    }

    /**
     * Loads a set of W3C Standard ontologies into the store (RDF, RDFA, RDFS, OWL, SKOS, SHACL, XSD)
     * and the SPARQL Syntax Vocabulary.
     */
    async loadFrameworkOntologies(executeInference: boolean = true): Promise<void> {
        this.loadTurtle(src.rdf, _RDF, executeInference);
        this.loadTurtle(src.rdfa, _RDFA, executeInference);
        this.loadTurtle(src.rdfs, _RDFS, executeInference);
        this.loadTurtle(src.owl, _OWL, executeInference);
        this.loadTurtle(src.sh, _SH, executeInference);
        this.loadTurtle(src.skos, _SKOS, executeInference);
        this.loadTurtle(src.sparql, _SPARQL, executeInference);
        this.loadTurtle(src.xsd, _XSD, executeInference);
    }

    /**
     * Add a quad to the store. Existing quads with the same subject, predicate, object, and graph will be ignored.
     * @param quad The quad to be added.
     * @returns The store instance.
     */
    add(quad: rdfjs.Quad): this {
        this._ds.add(quad);
        this._touchGraph(quad.graph.value);

        return this;
    }

    /**
     * Delete a quad from the store.
     * @param quad The quad to be deleted.
     * @returns The store instance.
     */
    delete(quad: rdfjs.Quad): this {
        this._ds.delete(quad);
        this._touchGraph(quad.graph.value);

        return this;
    }

    /**
     * Indicates if the store contains a specific quad.
     * @param quad The quad to be checked.
     * @returns `true` if the quad is found in the store, `false` otherwise.
     */
    has(quad: rdfjs.Quad): boolean {
        return this._ds.has(quad);
    }

    /**
     * Get the URIs of the graphs in the triple store.
     * @returns An array of graph URIs in no particular order.
     */
    getGraphs(): string[] {
        const result = new Set<string>();

        for (const q of this._ds.match(null, null, null, null)) {
            result.add(q.graph.value);
        }

        return Array.from(result);
    }

    /**
     * Load a set of triples into the store.
     * @param quads An array of quads to be loaded into the store.
     * @param graphUri The target graph URI.
     * @param executeInference Indicates if inference should be executed after loading the triples.
     * @param clearGraph Indicates if the graph should be cleared before loading.
     * @param onQuad A callback function that will be called for each parsed triple.
     */
    private _remapBlankNodes(quads: rdfjs.Quad[], graphUri: string): rdfjs.Quad[] {
        let h = 5381;
        for (let i = 0; i < graphUri.length; i++) {
            h = (h * 33 ^ graphUri.charCodeAt(i)) | 0;
        }
        const prefix = (h >>> 0).toString(36);
        const idMap = new Map<string, string>();
        const remap = (term: rdfjs.Term): rdfjs.Term => {
            if (term.termType !== 'BlankNode') return term;
            const key = term.value;
            if (!idMap.has(key)) {
                idMap.set(key, `${prefix}_${key}`);
            }
            return this.dataFactory.blankNode(idMap.get(key)!);
        };
        return quads.map(q => this.dataFactory.quad(
            remap(q.subject) as rdfjs.Quad_Subject,
            q.predicate,
            remap(q.object) as rdfjs.Quad_Object,
            q.graph
        ));
    }

    private _loadQuads(quads: rdfjs.Quad[], graphUri: string, executeInference: boolean = true, clearGraph: boolean = true, onQuad?: (quad: rdfjs.Quad) => void) {
        const graph = this.dataFactory.namedNode(graphUri.replace('\\', '\/'));

        // Only clear the graph once if there had been no errors.
        if (clearGraph) {
            // Note: Using the match method to clear the graph results in an error because the graph is being modified.
            this.deleteGraphs([graphUri]);

            if (this.reasoner) {
                const inferenceGraph = this.dataFactory.namedNode(this.reasoner.targetUriGenerator.getGraphUri(graphUri));

                this.deleteGraphs([inferenceGraph.value]);
            }
        }

        for (let q of quads) {
            this._ds.add(this.dataFactory.quad(q.subject, q.predicate, q.object, graph));

            if (onQuad) {
                onQuad(q);
            }
        }

        this._touchGraph(graph.value);

        if (this.reasoner && executeInference) {
            this.reasoner.expand(this._ds, graphUri);

            this._touchGraph(this.reasoner.targetUriGenerator.getGraphUri(graphUri));
        }
    }

    /**
     * Create an RDF store from Turtle, N3 or N-Triples data.
     * @param input Input string in Turtle, N3 or N-Triples format.
     * @param graphUri URI of the graph to in which the triples will be created.
     * @param executeInference Indicates if inference should be executed after loading the triples.
     * @param clearGraph Indicates if the graph should be cleared before loading.
     * @param onQuad Callback function that will be called for each parsed triple.
     * @returns A promise that resolves to an RDF store.
     */
    loadTurtle(input: string, graphUri: string, executeInference: boolean = true, clearGraph: boolean = true, onQuad?: (quad: rdfjs.Quad) => void): Store {
        const tokens = new TurtleLexer(createFileBlankNodeIdGenerator(graphUri)).tokenize(input).tokens;
        const cst = new TurtleParser().parse(tokens, false);
        const quads = new TurtleReader().visit(cst) as rdfjs.Quad[];

        this._loadQuads(quads, graphUri, executeInference, clearGraph, onQuad);

        return this;
    }

    /**
     * Create an RDF store from N-Quads data.
     * @param input Input string in N-Quads format.
     * @param graphUri URI of the default graph to use when quads have no graph component.
     * @param executeInference Indicates if inference should be executed after loading the triples.
     * @param clearGraph Indicates if the graph should be cleared before loading.
     * @param onQuad Callback function that will be called for each parsed triple.
     * @returns The store instance.
     */
    loadNQuads(input: string, graphUri: string, executeInference: boolean = true, clearGraph: boolean = true, onQuad?: (quad: rdfjs.Quad) => void): Store {
        const tokens = new NQuadsLexer(createFileBlankNodeIdGenerator(graphUri)).tokenize(input).tokens;
        const cst = new NQuadsParser().parse(tokens, false);
        const quads = new NQuadsReader().visit(cst) as rdfjs.Quad[];

        this._loadQuads(quads, graphUri, executeInference, clearGraph, onQuad);

        return this;
    }

    /**
     * Create an RDF store from RDF/XML data.
     * @param input Input data or stream format to be parsed.
     * @param graphUri URI of the graph to in which the triples will be created.
     * @param executeInference Indicates if inference should be executed after loading the triples.
     * @param clearGraph Indicates if the graph should be cleared before loading.
     * @param onQuad Callback function that will be called for each parsed triple.
     * @returns A promise that resolves to an RDF store.
     */
    async loadFromXmlStream(input: string | EventEmitter, graphUri: string, executeInference: boolean = true, clearGraph: boolean = true, onQuad?: (quad: rdfjs.Quad) => void): Promise<Store> {
        return new Promise((resolve, reject) => {
            let quads: rdfjs.Quad[] = [];

            const parser = new RdfXmlParser();

            if (typeof input === 'string') {
                parser.on('error', (error) => reject(error));
                parser.on('data', (quad) => quads.push(quad));
                parser.on('end', () => {
                    this._loadQuads(this._remapBlankNodes(quads, graphUri), graphUri, executeInference, clearGraph, onQuad);

                    resolve(this);
                });

                parser.write(input);
                parser.end();
            } else {
                parser.import(input)
                    .on('error', (error) => reject(error))
                    .on('data', (quad) => quads.push(quad))
                    .on('end', () => {
                        this._loadQuads(this._remapBlankNodes(quads, graphUri), graphUri, executeInference, clearGraph, onQuad);

                        resolve(this);
                    });
            }
        });
    }

    /**
     * Write the triples in the store into a string in Turtle format.
     * @param sourceGraphUri A graph URI.
     * @param targetFormat Optional mime type of the serialization format (e.g., 'text/turtle').
     * @param targetGraphUri Optional target graph URI. If not provided, the source graph URI will be used for serialization formats that support quads.
     * @param prefixes Optional prefixes to be used in the serialization.
     * @returns A string serialization of the triples in the graph in the specified format.
     */
    async serializeGraph(sourceGraphUri: string, targetFormat: string = 'text/turtle', targetGraphUri?: string, prefixes?: Record<string, string>): Promise<string> {
        return new Promise((resolve, reject) => {
            const sourceGraph = this.dataFactory.namedNode(sourceGraphUri);
            const targetGraph = targetGraphUri ? this.dataFactory.namedNode(targetGraphUri) : undefined;
            const store = graph();

            // Unfortunately the termType values of RDFJS terms are not directly compatible with rdflib.
            // For serialization, rdflib expectes a compareTerm function to sort blank nodes which does not
            // exist in the RDFJS interface or n3.Terms.
            const g = targetGraph ? toRdflibTerm(targetGraph) as any : undefined;

            for (const q of this._ds.match(null, null, null, sourceGraph)) {
                const s = toRdflibTerm(q.subject) as any;
                const p = toRdflibTerm(q.predicate) as any;
                const o = toRdflibTerm(q.object) as any;

                store.add(s, p, o, g);
            }

            const callback = (error: any, result?: string) => {
                if (error) {
                    reject(error);
                } else {
                    resolve(result ?? '');
                }
            };

            serialize(null, store as any, null, targetFormat, callback, prefixes);
        });
    }

    /**
     * Indicates if the store contains triples in a given graph.
     * @param graphUri A graph URI.
     * @returns `true` if the store contains triples in the graph URI, `false` otherwise.
     */
    hasGraph(graphUri: rdfjs.Quad_Graph | string): boolean {
        const uri = typeof graphUri === 'string' ? this.dataFactory.namedNode(graphUri) : graphUri;

        for (const _ of this._ds.match(null, null, null, uri)) {
            return true;
        }

        return false;
    }

    /**
     * Apply inference to the given graph and store the triples in the associated inference graph.
     * @param graphUri A graph URI.
     */
    executeInference(graphUri: rdfjs.Quad_Graph | string) {
        if (this.reasoner) {
            this.reasoner.expand(this._ds, graphUri);

            const uri = typeof graphUri === 'string' ? graphUri : graphUri.value;

            this._touchGraph(this.reasoner.targetUriGenerator.getGraphUri(uri));
        }
    }

    /**
     * Delete named graphs from the store.
     * @param graphUris URIs of the graphs to be deleted.
     */
    deleteGraphs(graphUris: string[]) {
        for (const graphUri of graphUris) {
            const graph = this.dataFactory.namedNode(graphUri);

            let deleted = false;

            for (const quad of this._ds.match(null, null, null, graph)) {
                this._ds.delete(quad);

                deleted = true;
            }

            if (deleted) {
                this._touchGraph(graphUri);
            }
        }
    }

    /**
     * Get the URIs of ordered list members in the store.
     * @param graphUris Optional graph URI or array of graph URIs to query.
     * @param listUri URI of the list to get the items from.
     * @returns An array of URIs of the items in the list.
     */
    getListItems(graphUris: string | string[] | undefined, listUri: string): string[] {
        // To do: Fix #10
        const list = listUri.includes(':') ?
            this.dataFactory.namedNode(listUri) :
            this.dataFactory.blankNode(listUri);

        return this._getListItems(graphUris, list).map(t => t.value);
    }

    /**
     * Recursively collect the items of an ordered list.
     * @param graphUris Optional graph URI or array of graph URIs to query.
     * @param subject The list node to collect the items from.
     * @param visited Set of list nodes that have already been visited.
     * @returns An array of the items in the list.
     */
    private _getListItems(graphUris: string | string[] | undefined, subject: rdfjs.Quad_Subject, visited: Set<string> = new Set<string>()): rdfjs.Quad_Subject[] {
        // Guard against cyclic lists, which would otherwise cause infinite recursion.
        if (visited.has(subject.value)) {
            return [];
        }

        visited.add(subject.value);

        const first = Array.from(this.matchAll(graphUris, subject, rdf.first, null));

        if (!first.length) {
            return [];
        }

        const rest = Array.from(this.matchAll(graphUris, subject, rdf.rest, null));

        const firstItem = first[0].object as rdfjs.Quad_Subject;
        const restList = rest[0]?.object as rdfjs.Quad_Subject | undefined;

        // A list node without a rdf:rest property is treated as the last node of the list.
        if (!restList || restList.value === RDF.nil) {
            return [firstItem];
        } else {
            const restItems = this._getListItems(graphUris, restList, visited);

            return [firstItem, ...restItems];
        }
    }

    /**
     * Returns the exact cardinality of the quads matching the pattern.
     * @param subject The optional subject.
     * @param predicate The optional predicate.
     * @param object The optional object.
     * @param graph The optional graph.
     */
    countQuads(subject?: rdfjs.Term | null, predicate?: rdfjs.Term | null, object?: rdfjs.Term | null, graph?: rdfjs.Term | null): number {
        return this._store.countQuads(subject, predicate, object, graph);
    }

    /**
     * Returns a lazy, read-only dataset view of the quads matching the given pattern.
     * Implements the RDF/JS `DatasetCore.match` semantics: the result reflects the
     * current contents of the store and does not copy any quads.
     * @param subject The optional subject.
     * @param predicate The optional predicate.
     * @param object The optional object.
     * @param graph The optional graph.
     * @returns A read-only dataset view of the matching quads.
     */
    match(subject?: rdfjs.Term | null, predicate?: rdfjs.Term | null, object?: rdfjs.Term | null, graph?: rdfjs.Term | null): DatasetView {
        return new DatasetView(this, {
            subject: subject ?? null,
            predicate: predicate ?? null,
            object: object ?? null,
            graphs: graph != null ? [graph as rdfjs.Quad_Graph] : undefined,
            includeInferred: false
        });
    }

    /**
     * Synchronously iterate the quads matching a single pattern.
     * @param subject The optional subject.
     * @param predicate The optional predicate.
     * @param object The optional object.
     * @param graph The optional graph.
     */
    *matchQuads(subject?: rdfjs.Term | null, predicate?: rdfjs.Term | null, object?: rdfjs.Term | null, graph?: rdfjs.Term | null): Generator<rdfjs.Quad> {
        yield* this._ds.match(subject, predicate, object, graph);
    }

    /**
     * Get an RDF/JS `Source` interface for the store that streams matching quads.
     * Primarily intended for query engines such as Comunica that operate on quad streams.
     * @returns A source that returns a stream of quads for a given pattern.
     */
    asSource(): rdfjs.Source<rdfjs.Quad> {
        return {
            match: (subject?: rdfjs.Term | null, predicate?: rdfjs.Term | null, object?: rdfjs.Term | null, graph?: rdfjs.Term | null) =>
                this._store.match(subject, predicate, object, graph)
        };
    }

    /**
     * Query the store for triples matching the given pattern supporting multiple graphs.
     * @param graphUris Optional graph URI or array of graph URIs to query.
     * @param subject A subject URI or null to match any subject.
     * @param predicate A predicate URI or null to match any predicate.
     * @param object An object URI or null to match any object.
     */
    *matchAll(graphUris: string | string[] | undefined, subject: rdfjs.Quad_Subject | null, predicate: rdfjs.Quad_Predicate | null, object: rdfjs.Quad_Object | null, includeInferred?: boolean) {
        if (includeInferred && !this.reasoner) {
            throw new Error('Reasoner is not available to include inferred triples.');
        }

        const s = subject as rdfjs.Term | null;
        const p = predicate as rdfjs.Term | null;
        const o = object as rdfjs.Term | null;

        if (graphUris !== undefined) {
            const graphs = Array.isArray(graphUris) ? graphUris : [graphUris];

            for (const graph of graphs.map(g => this.dataFactory.namedNode(g))) {
                for (const q of this._ds.match(s, p, o, graph)) {
                    yield q;
                }

                if (includeInferred !== false && this.reasoner) {
                    const inferenceGraphUri = this.reasoner.targetUriGenerator.getGraphUri(graph.value);
                    const inferenceGraph = this.dataFactory.namedNode(inferenceGraphUri);

                    for (let q of this._ds.match(s, p, o, inferenceGraph)) {
                        yield q;
                    }
                }
            }
        } else {
            yield* this._ds.match(s, p, o);
        }
    }

    /**
     * Indicate if there are triples matching the given pattern in the store.
     * @param graphUris Optional graph URI or array of graph URIs to query.
     * @param subject A subject URI or null to match any subject.
     * @param predicate A predicate URI or null to match any predicate.
     * @param object An object URI or null to match any object.
     * @returns `true` if there are triples matching the pattern, `false` otherwise.
     */
    any(graphUris: string | string[] | undefined, subject: rdfjs.Quad_Subject | null, predicate: rdfjs.Quad_Predicate | null, object: rdfjs.Quad_Object | null, includeInferred?: boolean): boolean {
        return this.matchAll(graphUris, subject, predicate, object, includeInferred).next().done === false;
    }

    /**
     * Get a read-only DatasetCore view of all quads in the specified graphs.
     * The view is lazy and live: it reflects the current contents of the store without
     * copying any quads. If you need an immutable snapshot, copy the view into a new
     * dataset, e.g. `RdfStore.createDefault().asDataset()`.
     * @param graphUris Optional graph URI or array of graph URIs. If undefined, all quads are included.
     * @param includeInferred Whether to include inferred triples. If undefined, inferred triples
     *  are included when a reasoner is available. If `true` without a reasoner, an error is thrown.
     * @returns A read-only dataset view of the matching quads.
     */
    getDataset(graphUris?: string | string[], includeInferred?: boolean): DatasetView {
        return new DatasetView(this, {
            graphUris: graphUris === undefined ? undefined : Array.isArray(graphUris) ? graphUris : [graphUris],
            includeInferred
        });
    }

    /**
     * Get the first triple matching the given pattern in the store.
     * @param graphUris Optional graph URI or array of graph URIs to query.
     * @param subject A subject URI or null to match any subject.
     * @param predicate A predicate URI or null to match any predicate.
     * @param object An object URI or null to match any object.
     * @returns The first triple that matches the pattern.
     * @throws {TripleNotFoundError} If no triple is found matching the pattern.
     */
    first(graphUris: string | string[] | undefined, subject: rdfjs.Quad_Subject | null, predicate: rdfjs.Quad_Predicate | null, object: rdfjs.Quad_Object | null, includeInferred?: boolean) {
        const result = this.matchAll(graphUris, subject, predicate, object, includeInferred).next().value;

        if (!result) {
            throw new TripleNotFoundError(subject, predicate, object);
        }

        return result;
    }
}